import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import { useFetch } from "./useFetch";
import { companiesApi, reviewsApi } from "@/api";
import type { Ratings } from "@/types";

export function useCompanyDetailPage(companyId: string | undefined) {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { notify } = useNotification();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    data: company,
    loading,
    error,
    refresh: refreshCompany,
  } = useFetch(() => companiesApi.getById(companyId!), [companyId]);

  const { data: reviewsData, refresh: refreshReviews } = useFetch(
    () => companiesApi.getReviews(companyId!, 0, 100),
    [companyId],
  );

  const hasReviewed = reviewsData?.items.some((r) => r.userId === user?.id);

  const handleSubmitReview = useCallback(
    async (ratings: Ratings, comment?: string) => {
      if (!companyId) return;
      setSubmitting(true);
      try {
        await reviewsApi.create({ companyId, ratings, comment });
        notify(t("review.reviewSubmitSuccess"), "success");
        setShowForm(false);
        refreshCompany();
        refreshReviews();
      } catch {
        notify(t("common.error"), "error");
      } finally {
        setSubmitting(false);
      }
    },
    [companyId, notify, refreshCompany, refreshReviews, t],
  );

  return {
    company,
    loading,
    error,
    reviewsData,
    showForm,
    setShowForm,
    submitting,
    hasReviewed,
    handleSubmitReview,
    isAuthenticated,
  };
}
